import {
    Icon,
    Text,
    ButtonGhost,
    Colors,
    Heading,
    type ColorType,
    formatDate,
    DATE_FORMATS,
} from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';

import type { ProficiencyLevel } from '@/types/competencies/ProficiencyLevel';
import type { CompetencyResult } from '@/types/review/CompetencyGroupResult';

import { CustomPill } from '@/components/CustomPill/CustomPill';
import { ProficiencyIcon } from '@/components/ProficiencyIcon/ProficiencyIcon';
import { ProficiencyLevelBars } from '@/components/ProficiencyIcon/ProficiencyLevelBars';
import { resolveProficiencyLevelColor } from '@/components/ProficiencyIcon/resolveProficiencyLevelColor';
import { getIconEntry } from '@/constants/proficiencyIconCatalog';
import { useSelectedAppLanguage } from '@/hooks';
import { getLocalizedText } from '@/utils/localization';
import { orderProficiencyLevels } from '@/utils/proficiencyLevel';

interface CompetencyItemProps {
    competency: CompetencyResult;
    employeeId?: string;
    proficiencyLevels: ProficiencyLevel[];
    isReadOnly?: boolean;
    onHistoryClick: (competency: CompetencyResult) => void;
    onEditClick: (competency: CompetencyResult, e: React.MouseEvent) => void;
}

export const CompetencyItem = ({
    competency,
    employeeId,
    proficiencyLevels,
    isReadOnly = false,
    onHistoryClick,
    onEditClick,
}: CompetencyItemProps) => {
    const { t } = useTranslation();
    const selectedLanguage = useSelectedAppLanguage();
    const isRemoved = competency.isDeactivated || competency.isDeleted;
    // Prefer the Quick Assessment rating (set by transformCompetencyResults from the manager's
    // assessment), falling back to the historical profile level for unassessed competencies.
    // Without this, additional competencies (which have no lastAssessedProficiencyLevelId) would
    // always show a blank rating — see Bug #14282.
    const historicalProficiencyLevel =
        competency.proficiencyLevel ??
        (competency.lastAssessedProficiencyLevelId
            ? proficiencyLevels.find(
                  (level) => level.id === competency.lastAssessedProficiencyLevelId,
              )
            : null) ??
        null;

    const iconEntry = historicalProficiencyLevel
        ? getIconEntry(historicalProficiencyLevel.icon)
        : undefined;
    const orderedLevels = orderProficiencyLevels(proficiencyLevels);
    const levelIndex = historicalProficiencyLevel
        ? orderedLevels.findIndex((level) => level.id === historicalProficiencyLevel.id)
        : -1;
    const levelColor = resolveProficiencyLevelColor({
        iconColor: historicalProficiencyLevel?.iconColor,
        levelIndex,
    });

    const handleItemClick = () => {
        if (employeeId) {
            onHistoryClick(competency);
        }
    };

    const handleEditButtonClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isReadOnly) {
            return;
        }
        onEditClick(competency, e);
    };

    return (
        <div
            className={
                'flex cursor-pointer flex-col items-start justify-between gap-[16px] border-b border-gray-200 bg-white p-[16px] transition-all last:border-b-0 hover:bg-gray-50 sm:flex-row sm:items-center'
            }
            onClick={handleItemClick}
            role={'button'}
            tabIndex={0}
            data-testid={`competency-item-${competency.id}`}
            // A removed competency is only struck through visually; this states the same in the DOM.
            data-removed={isRemoved}
        >
            <div
                className={
                    'flex min-w-0 flex-1 items-start gap-[12px] sm:items-center sm:gap-[16px]'
                }
            >
                {historicalProficiencyLevel ? (
                    <div className={'flex flex-col items-center gap-1'}>
                        <ProficiencyIcon
                            iconEntry={iconEntry}
                            iconUrl={historicalProficiencyLevel.iconUrl}
                            color={levelColor}
                        />
                        <ProficiencyLevelBars
                            levelIndex={levelIndex}
                            totalLevels={orderedLevels.length}
                            color={levelColor}
                            dataTestId={`competency-item-${competency.id}-proficiency-bars`}
                        />
                    </div>
                ) : (
                    <Icon
                        name={'essential-minus'}
                        color={Colors.BASE_400}
                        size={'lg'}
                    />
                )}

                <div className={'min-w-0 flex-1'}>
                    <div className={'flex flex-col gap-2'}>
                        <div
                            className={
                                'flex flex-col items-start gap-2 font-bold sm:flex-row sm:items-center sm:gap-4'
                            }
                        >
                            <Heading
                                level={6}
                                color={isRemoved ? undefined : Colors.NAVY}
                                className={`text-body-sm! font-semibold ${isRemoved ? 'text-gray-400 line-through' : ''}`}
                            >
                                {getLocalizedText(competency.name)}
                            </Heading>
                            <div className={'flex items-center gap-2'}>
                                {historicalProficiencyLevel ? (
                                    <CustomPill
                                        color={historicalProficiencyLevel.iconColor as ColorType}
                                    >
                                        {getLocalizedText(historicalProficiencyLevel.label)}
                                    </CustomPill>
                                ) : (
                                    <Text
                                        size={'default'}
                                        color={Colors.BASE_400}
                                    >
                                        {t('common.notDefined')}
                                    </Text>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className={'flex flex-shrink-0 items-center gap-2 self-end sm:self-auto'}>
                {competency.lastAssessedAt && (
                    <Text
                        size={'default'}
                        color={Colors.BASE_400}
                        className={'flex items-center gap-[4px] text-[13px] whitespace-nowrap'}
                    >
                        <Icon
                            name={'time-clock'}
                            color={Colors.BASE_400}
                            size={16}
                        />
                        {formatDate({
                            date: competency.lastAssessedAt,
                            format: DATE_FORMATS.DISPLAY_DATE,
                            language: selectedLanguage,
                        })}
                    </Text>
                )}
                {!isReadOnly && (
                    <ButtonGhost
                        onClick={handleEditButtonClick}
                        fitContent
                        size={'sm'}
                        dataTestId={'competency-edit-button'}
                        leftIcon={
                            <Icon
                                name={'money-icon_pencil'}
                                color={Colors.BASE_400}
                                size={16}
                            />
                        }
                    />
                )}
            </div>
        </div>
    );
};

CompetencyItem.displayName = 'CompetencyItem';
